import { arrayMove } from '@dnd-kit/sortable';
import { useCallback, useMemo } from 'react';

import { DataTableColumn } from '~components/DataTable/dataTableColumn';
import useDataTableSavedState from '~components/DataTable/useDataTableSavedState';

type DataTableColumnLayout = Pick<
  DataTableColumn<never>,
  'defaultHidden' | 'field' | 'headerName' | 'hideable' | 'pin'
>;

function countVisibleScrollingColumns(
  columns: Array<Pick<DataTableColumn<never>, 'field' | 'pin'>>,
  visibility: Record<string, boolean>,
) {
  return columns.filter((column) => !column.pin && visibility[column.field] !== false)
    .length;
}

export default function useDataTableColumnLayout<TColumn extends DataTableColumnLayout>(
  tableId: string,
  columns: TColumn[],
) {
  const defaultOrder = useMemo(() => columns.map((column) => column.field), [columns]);
  const defaultVisibility = useMemo(
    () =>
      Object.fromEntries(
        columns
          .filter((column) => column.defaultHidden)
          .map(({ field }) => [field, false]),
      ),
    [columns],
  );
  const {
    columnOrder,
    columnVisibility: savedColumnVisibility,
    columnWidths,
    reset,
    setColumnOrder,
    setColumnVisibility,
    setColumnWidth,
  } = useDataTableSavedState(tableId, defaultOrder, defaultVisibility);

  const columnVisibility = useMemo(
    () =>
      countVisibleScrollingColumns(columns, savedColumnVisibility) > 0
        ? savedColumnVisibility
        : defaultVisibility,
    [columns, defaultVisibility, savedColumnVisibility],
  );

  const columnsByField = useMemo(
    () => new Map(columns.map((column) => [column.field, column])),
    [columns],
  );

  const pinnedColumns = useMemo(
    () => ({
      left: columns.filter((column) => column.pin === 'left'),
      right: columns.filter((column) => column.pin === 'right'),
    }),
    [columns],
  );

  const moveColumn = useCallback(
    (movedField: string, targetField: string) => {
      setColumnOrder((order) =>
        arrayMove(order, order.indexOf(movedField), order.indexOf(targetField)),
      );
    },
    [setColumnOrder],
  );

  const toggleColumn = useCallback(
    (field: string) => {
      setColumnVisibility((visibility) => {
        const isHiding = visibility[field] !== false;
        const nextVisibility = { ...visibility, [field]: !isHiding };
        const wouldHideEveryColumn =
          countVisibleScrollingColumns(columns, nextVisibility) === 0;

        return wouldHideEveryColumn ? visibility : nextVisibility;
      });
    },
    [columns, setColumnVisibility],
  );

  const chooserColumns = useMemo(() => {
    const orderedFields = [
      ...pinnedColumns.left.map(({ field }) => field),
      ...columnOrder.filter((field) => !columnsByField.get(field)?.pin),
      ...pinnedColumns.right.map(({ field }) => field),
    ];

    return orderedFields.flatMap((field) => {
      const column = columnsByField.get(field);
      const isFixedColumn = column?.pin && column.hideable === false;

      if (!column || isFixedColumn) {
        return [];
      }

      const isVisible = columnVisibility[field] !== false;
      const isLastVisibleColumn =
        isVisible &&
        !column.pin &&
        countVisibleScrollingColumns(columns, columnVisibility) === 1;

      return {
        field,
        headerName: column.headerName,
        isHideable: (column.hideable ?? true) && !isLastVisibleColumn,
        isVisible,
        pin: column.pin,
      };
    });
  }, [columnOrder, columnVisibility, columns, columnsByField, pinnedColumns]);

  return {
    chooserColumns,
    columnOrder,
    columnsByField,
    columnVisibility,
    columnWidths,
    moveColumn,
    pinnedColumns,
    reset,
    setColumnWidth,
    toggleColumn,
  };
}
